import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fetchClassGradingStatus, gradeClassPendingSessions } from '../../utils/classApi';
import { getCurrentUserEmail } from '../../utils/currentUser';
import './ClassGradingProgress.css';

// Sessions are otherwise only AI-graded when someone opens them on Student
// Detail, so class trends/exports would miss everything nobody has viewed.
// This drives the backend's batch grader for the class being shown: check the
// counts, then call grade-pending one batch at a time until nothing is pending,
// stopping early on errors, on a round where the AI is failing, or when the
// user leaves the class. onGraded fires once the loop stops after grading
// anything, so the page can refetch whatever reads graded results.
function ClassGradingProgress({ classId, onGraded }) {
  const [counts, setCounts] = useState(null); // { total, graded, pending, ungradable }
  const [phase, setPhase] = useState('loading'); // 'loading' | 'running' | 'done' | 'paused' | 'hidden'
  const [runToken, setRunToken] = useState(0); // bumped by Retry to restart the loop

  // The one in-flight loop: { classId, controller }. Guards against a second
  // loop for the same class (re-renders, StrictMode's double effect, a repeat
  // Manage click) — cleared on cleanup so a StrictMode remount can start fresh.
  const activeLoopRef = useRef(null);
  const onGradedRef = useRef(onGraded);
  onGradedRef.current = onGraded;

  useEffect(() => {
    if (activeLoopRef.current?.classId === classId) return undefined;

    const email = getCurrentUserEmail();
    if (!email) {
      setPhase('hidden');
      return undefined;
    }

    const controller = new AbortController();
    const loop = { classId, controller };
    activeLoopRef.current = loop;

    (async () => {
      let gradedAny = false;
      let latest = null;
      try {
        latest = (await fetchClassGradingStatus(classId, email, controller.signal)).data;
        setCounts(latest);
        if (latest.pending === 0) {
          setPhase('done');
          return;
        }
        setPhase('running');

        while (latest.pending > 0) {
          const previousPending = latest.pending;
          // No client timeout on purpose: a batch can legitimately take ~45s.
          const res = (await gradeClassPendingSessions(classId, email, controller.signal)).data;
          latest = res;
          setCounts(res);
          if (res.gradedNow > 0) gradedAny = true;
          // Stop if the AI failed every session it tried this round (back off
          // rather than hammer it), or if the round made no headway at all —
          // calling again would just loop. A round of only newly-ungradable
          // sessions (gradedNow 0, failedNow 0, pending down) is progress.
          const aiFailing = res.gradedNow === 0 && res.failedNow > 0;
          const stalled = res.pending > 0 && res.pending >= previousPending;
          if (aiFailing || stalled) {
            setPhase('paused');
            return;
          }
        }
        setPhase('done');
      } catch (err) {
        if (axios.isCancel(err)) return;
        // Couldn't even read the counts (403 for this user, 404, 500): nothing
        // useful to show. Once grading has started, keep the bar and offer Retry.
        setPhase(latest ? 'paused' : 'hidden');
      } finally {
        if (activeLoopRef.current === loop) activeLoopRef.current = null;
        if (gradedAny && !controller.signal.aborted) onGradedRef.current?.();
      }
    })();

    return () => {
      controller.abort();
      if (activeLoopRef.current === loop) activeLoopRef.current = null;
    };
  }, [classId, runToken]);

  // Shown immediately (same muted line as the "All N graded" summary) so the
  // ~1s status check doesn't make the bar pop in and push the page down.
  if (phase === 'loading') return <div className="grading-summary">Checking grading status…</div>;
  if (phase === 'hidden' || !counts || counts.total === 0) return null;

  const gradable = counts.total - counts.ungradable;
  const ungradableNote = counts.ungradable > 0
    ? `${counts.ungradable} submission${counts.ungradable === 1 ? '' : 's'} couldn't be graded (e.g. empty transcript)`
    : null;

  if (phase === 'done') {
    return (
      <div className="grading-summary">
        {gradable > 0 && `All ${gradable} submission${gradable === 1 ? '' : 's'} graded`}
        {gradable > 0 && counts.ungradable > 0 && ' · '}
        {counts.ungradable > 0 && `${counts.ungradable} couldn't be graded`}
      </div>
    );
  }

  const percent = gradable > 0 ? Math.min(100, (counts.graded / gradable) * 100) : 0;

  return (
    <div className="grading-progress" role="status" aria-live="polite">
      <div className="grading-progress-header">
        <span className="grading-progress-label">
          {phase === 'paused'
            ? 'Grading paused. Some submissions are still ungraded.'
            : 'Grading submissions…'}
        </span>
        <span className="grading-progress-count">
          {counts.graded} of {gradable}
          {phase === 'paused' && (
            <button className="grading-progress-retry" onClick={() => { setPhase('running'); setRunToken((t) => t + 1); }}>
              Retry
            </button>
          )}
        </span>
      </div>
      <div
        className="grading-progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={gradable}
        aria-valuenow={counts.graded}
      >
        <div className="grading-progress-fill" style={{ width: `${percent}%` }} />
      </div>
      {ungradableNote && <div className="grading-progress-note">{ungradableNote}</div>}
    </div>
  );
}

export default ClassGradingProgress;
